import { RegisterOptions, useFormContext } from "react-hook-form";
import { Button, CheckBox, Font } from "@moneyforward/mfx_biz_react_components";
import {
  KanaNameFormField,
  NameFormField,
  PasswordFormField,
  MiddleNameFormField,
  KanaMiddleNameFormField,
  AuthorityBlock,
  EmailBlock,
  UsernameLabel,
} from "@/components/molecules";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useIsSpMode } from "@/hooks";
import { css } from "@emotion/react";
import { color } from "@/styles/theme";
import { Authority } from "@/generated/graphql";
import { CancelButton, DeleteConfirmModal } from "@/components/atoms";

type Props = {
  username: string;
  authority: Authority;
  email: string;
  waitingForChangeToAdmin: boolean;
  isOwnUserProfile: boolean;
  onSubmit: (formValues: EditUserFormValues) => void;
  onClickChangeToMember: () => void;
  onClickChangeToAdmin: () => void;
  onClickDelete: () => void;
  onClickChangeEmailAddress?: () => void;
  onClickResendEmail?: () => void;
  submitting: boolean;
  passwordRegisterOptions?: RegisterOptions;
  onInputPassword?: (event: ChangeEvent<HTMLInputElement>) => void;
  meIsAdminUsersAtMikatanoID: boolean;
  userIsAdminUsersAtMikatanoID: boolean;
};

export type EditUserFormValues = {
  password: string;
  firstName: string;
  firstNameKana: string;
  middleName: string;
  middleNameKana: string;
  lastName: string;
  lastNameKana: string;
};

export const EditUserForm = (props: Props) => {
  const isSpMode = useIsSpMode();
  const { getValues, handleSubmit } = useFormContext();
  const [deleteConfirmModalActive, setDeleteConfirmModalActive] =
    useState<boolean>(false);
  const [usePasswordMeter, setUsePasswordMeter] = useState<boolean>(true);

  const onClickButtonInModal = () => {
    setDeleteConfirmModalActive(false);
  };

  useEffect(() => {
    setUsePasswordMeter(getValues(["password"]).shift()?.length !== 0);
  }, [getValues(["password"])]);

  const [enableDelete, setEnableDelete] = useState<boolean>(false);
  const [displayErrorTxt, setDisplayErrorTxt] = useState<boolean>();
  const deleteModalBody = (
    <div css={deleteModalWrapper}>
      <div>
        <Font>利用者を削除しますか？</Font>
        <br />
        <Font>※ 削除前に必ずご確認ください</Font>
        <Font>
          Mikatano
          IDから利用者削除を行う際は、先にご利用中サービスの利用者一覧から該当の利用者の状態を利用停止に変更してください。
        </Font>
      </div>
      <div css={deleteModalCheckbox}>
        <CheckBox
          checked={enableDelete}
          onChange={() => {
            setEnableDelete(!enableDelete);
            if (displayErrorTxt !== undefined) {
              setDisplayErrorTxt(enableDelete);
            }
          }}
          bgSize="sm"
        />
        <Font>この動作は取り消すことができません</Font>
      </div>
      {displayErrorTxt && (
        <div>
          <Font variant="s" css={displayErrorTxtColor}>
            チェックを入れてください。
          </Font>
        </div>
      )}
    </div>
  );

  return (
    <div css={container}>
      <div css={!isSpMode ? box : spModeBox}>
        <form
          onSubmit={handleSubmit(props.onSubmit)}
          css={form}
          autoComplete="off"
        >
          <div css={!isSpMode ? inputList : spModeInputList}>
            <div css={listItem}>
              <NameFormField
                nameLastName="lastName"
                nameFirstName="firstName"
              />
            </div>
            <div css={listItem}>
              <KanaNameFormField
                nameLastName="lastNameKana"
                nameFirstName="firstNameKana"
              />
            </div>
            <div css={listItem}>
              <MiddleNameFormField name="middleName" />
            </div>
            <div css={listItem}>
              <KanaMiddleNameFormField name="middleNameKana" />
            </div>
            <div css={listItem}>
              {props.authority === Authority.Admin && (
                <EmailBlock
                  email={props.email}
                  onClick={props.onClickChangeEmailAddress}
                  loading={props.submitting}
                  editable={true}
                  disableRequiredLabel={true}
                />
              )}
            </div>
            <div
              css={{ borderTop: "1px solid #DADEDF", margin: "10px 0 20px 0" }}
            />
            <div css={listItem}>
              <UsernameLabel username={props.username} />
            </div>
            <div css={listItem}>
              <PasswordFormField
                name="password"
                usePasswordMeter={usePasswordMeter}
                registerOptions={props.passwordRegisterOptions}
                onInput={props.onInputPassword}
                labelText={
                  props.isOwnUserProfile
                    ? ""
                    : "パスワードを変更した場合は、7日以内にログインしてください"
                }
              />
            </div>
            <div
              css={{ borderTop: "1px solid #DADEDF", margin: "10px 0 20px 0" }}
            />
            <div css={listItem}>
              <AuthorityBlock
                authority={props.authority}
                waitingForChangeToAdmin={props.waitingForChangeToAdmin}
                onClickChangeToMember={props.onClickChangeToMember}
                onClickChangeToAdmin={props.onClickChangeToAdmin}
                onClickResendEmail={props.onClickResendEmail}
                loading={props.submitting}
                meIsAdminUsersAtMikatanoID={props.meIsAdminUsersAtMikatanoID}
                userIsAdminUsersAtMikatanoID={
                  props.userIsAdminUsersAtMikatanoID
                }
              />
            </div>
          </div>
          <div css={buttonWrapper}>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setDeleteConfirmModalActive(true);
              }}
              disabled={props.submitting}
            >
              削除
            </Button>
            <CancelButton />
            <Button
              type="submit"
              variant="primary"
              css={buttonMargin}
              disabled={props.submitting}
            >
              保存
            </Button>
          </div>
        </form>
      </div>

      <DeleteConfirmModal
        active={deleteConfirmModalActive}
        title="利用者の削除"
        body={deleteModalBody}
        onClickCancel={onClickButtonInModal}
        onClickDelete={() => {
          if (!enableDelete) {
            setDisplayErrorTxt(true);
            return;
          }
          props.onClickDelete();
          onClickButtonInModal();
        }}
      />
    </div>
  );
};

const container = css({
  display: "flex",
  justifyContent: "center",
});

const box = css({
  width: "640px",
  minHeight: "245px",
  padding: "40px 32px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const spModeBox = css({
  width: "640px",
  minHeight: "245px",
  padding: "15px 17px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const form = css({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
});

const inputList = css({
  flex: 1,
});

const spModeInputList = css({
  flex: 1,
});

const listItem = css({
  marginBottom: "15px",
});

const buttonWrapper = css({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "8px",
});
const buttonMargin = css({
  marginLeft: "16px",
});

const deleteModalWrapper = css({
  marginTop: "16px",
});

const deleteModalCheckbox = css({
  display: "flex",
  alignItems: "center",
  marginTop: "26px",
});

const displayErrorTxtColor = css({
  color: color.attention,
});
